import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { 
  getConversations, 
  getConversationMessages, 
  sendMessage, 
  markMessageRead,
  getUnreadCount,
  getFoodTrucks
} from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  MessageCircle,
  Send,
  Plus,
  Users,
  Truck,
  Clock,
  CheckCheck,
  Loader2,
  Inbox,
  Search,
  ArrowLeft,
} from "lucide-react";
import { format, parseISO, isToday, isYesterday } from "date-fns";
import { fr } from "date-fns/locale";

export default function MessagesPage() {
  const { user, isAdmin, isBureau } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [newConversation, setNewConversation] = useState({
    destinataire_id: "",
    sujet: "",
    contenu: ""
  });
  const messagesEndRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    loadConversations();
    if (isAdmin() || isBureau()) {
      loadFoodTrucks();
    } else {
      // For members, set default recipient to bureau
      setNewConversation(prev => ({ ...prev, destinataire_id: "bureau" }));
    }
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const loadConversations = async () => {
    try {
      const response = await getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error("Error loading conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFoodTrucks = async () => {
    try {
      const response = await getFoodTrucks();
      setFoodTrucks(response.data.filter(ft => ft.statut === "actif"));
    } catch (error) {
      console.error("Error loading food trucks:", error);
    }
  };

  const loadMessages = async (participantId) => {
    setLoadingMessages(true);
    try {
      const response = await getConversationMessages(participantId);
      setMessages(response.data);
      // Refresh conversations to update unread count
      loadConversations();
    } catch (error) {
      toast.error("Erreur de chargement des messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    loadMessages(conv.participant_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      await sendMessage({
        destinataire_id: selectedConversation.participant_id,
        sujet: "Re: Conversation",
        contenu: newMessage
      });
      setNewMessage("");
      loadMessages(selectedConversation.participant_id);
      toast.success("Message envoyé");
    } catch (error) {
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleNewConversation = async (e) => {
    e.preventDefault();
    if (!newConversation.destinataire_id || !newConversation.sujet || !newConversation.contenu) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setSending(true);
    try {
      await sendMessage(newConversation);
      setShowNewDialog(false);
      // Reset form but keep bureau as default for members
      if (isAdmin() || isBureau()) {
        setNewConversation({ destinataire_id: "", sujet: "", contenu: "" });
      } else {
        setNewConversation({ destinataire_id: "bureau", sujet: "", contenu: "" });
      }
      loadConversations();
      toast.success("Message envoyé");
    } catch (error) {
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const formatMessageDate = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) {
      return format(date, "HH:mm", { locale: fr });
    } else if (isYesterday(date)) {
      return "Hier " + format(date, "HH:mm", { locale: fr });
    } else {
      return format(date, "d MMM HH:mm", { locale: fr });
    }
  };

  const formatConversationDate = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) {
      return format(date, "HH:mm", { locale: fr });
    } else if (isYesterday(date)) {
      return "Hier";
    } else {
      return format(date, "d MMM", { locale: fr });
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participant_nom.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isFromMe = (msg) => {
    if (user?.food_truck_id) {
      return msg.expediteur_id === user.food_truck_id;
    }
    return msg.expediteur_id === user?.id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="messages-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-secondary">
            Messages
          </h1>
          <p className="text-muted-foreground">
            {isAdmin() || isBureau() 
              ? "Communication avec les adhérents" 
              : "Contactez le bureau Food Truck United"}
          </p>
        </div>
        <Button 
          onClick={() => setShowNewDialog(true)}
          className="bg-primary hover:bg-primary-hover"
          data-testid="new-message-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau message
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-250px)] min-h-[500px]">
        {/* Conversations List */}
        <Card className={`bg-white border-[#E5E0DC] ${selectedConversation && isMobile ? 'hidden md:block' : ''}`}>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg text-secondary flex items-center gap-2">
              <Inbox className="w-5 h-5 text-primary" />
              Conversations
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 border-[#E5E0DC]"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-400px)] min-h-[350px]">
              {filteredConversations.length > 0 ? (
                <div className="divide-y divide-[#E5E0DC]">
                  {filteredConversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`p-4 cursor-pointer hover:bg-[#F9F7F5] transition-colors ${
                        selectedConversation?.id === conv.id ? "bg-primary/5 border-l-4 border-primary" : ""
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            conv.participant_type === "bureau" ? "bg-highlight/10" : "bg-primary/10"
                          }`}>
                            {conv.participant_type === "bureau" ? (
                              <Users className="w-5 h-5 text-highlight" />
                            ) : (
                              <Truck className="w-5 h-5 text-primary" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-secondary truncate">
                              {conv.participant_nom}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {conv.dernier_message}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatConversationDate(conv.updated_at)}
                          </span>
                          {conv.non_lus > 0 && (
                            <Badge className="bg-primary text-white text-xs px-2 py-0.5">
                              {conv.non_lus}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune conversation</p>
                  <p className="text-sm mt-1">Commencez par envoyer un message</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Messages Area */}
        <Card className={`bg-white border-[#E5E0DC] md:col-span-2 flex flex-col ${!selectedConversation && isMobile ? 'hidden md:flex' : ''}`}>
          {selectedConversation ? (
            <>
              {/* Conversation Header */}
              <CardHeader className="pb-3 border-b border-[#E5E0DC]">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="md:hidden"
                    onClick={() => setSelectedConversation(null)}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedConversation.participant_type === "bureau" ? "bg-highlight/10" : "bg-primary/10"
                  }`}>
                    {selectedConversation.participant_type === "bureau" ? (
                      <Users className="w-5 h-5 text-highlight" />
                    ) : (
                      <Truck className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="font-heading text-lg text-secondary">
                      {selectedConversation.participant_nom}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {selectedConversation.participant_type === "bureau" 
                        ? "Bureau Food Truck United" 
                        : "Food Truck"}
                    </p>
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 p-0 overflow-hidden">
                <ScrollArea className="h-[calc(100vh-480px)] min-h-[250px] p-4">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : messages.length > 0 ? (
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${isFromMe(msg) ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                              isFromMe(msg)
                                ? "bg-primary text-white rounded-br-md"
                                : "bg-[#F0EBE6] text-secondary rounded-bl-md"
                            }`}
                          >
                            {msg.sujet && !msg.sujet.startsWith("Re:") && (
                              <p className={`text-xs font-semibold mb-1 ${
                                isFromMe(msg) ? "text-white/80" : "text-primary"
                              }`}>
                                {msg.sujet}
                              </p>
                            )}
                            <p className="whitespace-pre-wrap">{msg.contenu}</p>
                            <div className={`flex items-center gap-1 mt-1 text-xs ${
                              isFromMe(msg) ? "text-white/70 justify-end" : "text-muted-foreground"
                            }`}>
                              <Clock className="w-3 h-3" />
                              <span>{formatMessageDate(msg.created_at)}</span>
                              {isFromMe(msg) && msg.lu && (
                                <CheckCheck className="w-3 h-3 ml-1" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <p>Aucun message dans cette conversation</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>

              {/* Message Input */}
              <div className="p-4 border-t border-[#E5E0DC]">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrivez votre message..."
                    className="flex-1 border-[#E5E0DC]"
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={sending || !newMessage.trim()}
                    className="bg-primary hover:bg-primary-hover"
                    data-testid="send-message-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">Sélectionnez une conversation</p>
                <p className="text-sm mt-1">ou créez un nouveau message</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New Message Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              Nouveau message
            </DialogTitle>
            <DialogDescription>
              {isAdmin() || isBureau() 
                ? "Envoyez un message à un food truck" 
                : "Contactez le bureau Food Truck United"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleNewConversation} className="space-y-4">
            {/* Destinataire */}
            {isAdmin() || isBureau() ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-secondary">Destinataire</label>
                <Select
                  value={newConversation.destinataire_id}
                  onValueChange={(val) => setNewConversation({ ...newConversation, destinataire_id: val })}
                >
                  <SelectTrigger className="border-[#E5E0DC]">
                    <SelectValue placeholder="Sélectionner un food truck" />
                  </SelectTrigger>
                  <SelectContent>
                    {foodTrucks.map((ft) => (
                      <SelectItem key={ft.id} value={ft.id}>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-primary" />
                          {ft.nom_food_truck}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="p-3 bg-[#F9F7F5] rounded-lg flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-highlight/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-highlight" />
                </div>
                <div>
                  <p className="font-semibold text-secondary">Bureau Food Truck United</p>
                  <p className="text-sm text-muted-foreground">Administration</p>
                </div>
              </div>
            )}

            {/* Sujet */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary">Sujet</label>
              <Input
                value={newConversation.sujet}
                onChange={(e) => setNewConversation({ ...newConversation, sujet: e.target.value })}
                placeholder="Sujet du message"
                className="border-[#E5E0DC]"
                data-testid="new-message-subject"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary">Message</label>
              <Textarea
                value={newConversation.contenu}
                onChange={(e) => setNewConversation({ ...newConversation, contenu: e.target.value })}
                placeholder="Écrivez votre message..."
                className="border-[#E5E0DC] min-h-[150px]"
                data-testid="new-message-content"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewDialog(false)}
                className="border-[#E5E0DC]"
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={sending}
                className="bg-primary hover:bg-primary-hover"
                data-testid="send-new-message-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
